// frontend/src/utils/phoneUtils.js

/**
 * Phone display helpers for SG-first HR surfaces.
 *
 * Storage / API may hold E.164 (`+6597153045`), bare local (`97153045`), or
 * lightly formatted strings. Display MUST go through these helpers — never
 * invent a local `formatPhone` in a page/component (same rule as dateUtils).
 */

/**
 * Strip common SG country-code prefixes and non-digits for display normalisation.
 *
 * @param {string|null|undefined} phone
 * @returns {string} Digits only, with leading 65 removed when it yields 8 local digits
 */
export const toLocalSgDigits = (phone) => {
  if (!phone) return '';
  const digitsOnly = String(phone).replace(/\D/g, '');
  if (digitsOnly.startsWith('65') && digitsOnly.length === 10) {
    return digitsOnly.slice(2);
  }
  return digitsOnly;
};

/**
 * Human-readable phone for tables, cards, and detail views.
 *
 * Singapore 8-digit numbers → `XXXX XXXX` (IMDA / common SG grouping).
 * Non-SG or irregular lengths are returned trimmed as stored (no forced grouping).
 *
 * @param {string|null|undefined} phone
 * @returns {string}
 */
export const formatPhoneForDisplay = (phone) => {
  if (phone === null || phone === undefined) return '';
  const trimmed = String(phone).trim();
  if (!trimmed) return '';

  const local = toLocalSgDigits(trimmed);
  if (local.length === 8) {
    return `${local.slice(0, 4)} ${local.slice(4)}`;
  }

  // Preserve original when we cannot confidently treat it as SG local
  return trimmed;
};

/**
 * True for an 8-digit Singapore mobile (starts with 8 or 9).
 *
 * @param {string|null|undefined} phone
 * @returns {boolean}
 */
export const isSgMobileNumber = (phone) => {
  const local = toLocalSgDigits(phone);
  return local.length === 8 && (local.startsWith('8') || local.startsWith('9'));
};

/**
 * True for an 8-digit office direct-dial (starts with 6).
 *
 * @param {string|null|undefined} phone
 * @returns {boolean}
 */
export const isSgOfficeDid = (phone) => {
  const local = toLocalSgDigits(phone);
  return local.length === 8 && local.startsWith('6');
};

/**
 * Separate the company mobile from the office direct line.
 *
 * A 6xxx value still stored in Work Phone is treated as the direct line
 * until it has been copied into ``did_number``. The extension belongs to
 * that line and is never glued onto the mobile.
 *
 * @param {{ work_phone_number?: string, did_number?: string, extension_number?: string }|null|undefined} person
 * @returns {{ mobile: string, did: string, extension: string }}
 */
export const splitWorkContact = (person) => {
  const workRaw = person?.work_phone_number || '';
  const didRaw = person?.did_number || '';
  const extension = String(person?.extension_number || '').trim();
  const workDigits = toLocalSgDigits(workRaw);
  const explicitDid = toLocalSgDigits(didRaw);
  const didDigits = explicitDid || (isSgOfficeDid(workDigits) ? workDigits : '');
  const did = explicitDid ? didRaw : (didDigits ? workRaw : '');
  const mobile = isSgMobileNumber(workDigits) && workDigits !== didDigits ? workRaw : '';
  return {
    mobile,
    did,
    extension: didDigits ? extension : '',
  };
};

/**
 * Direct line plus the extension that rings on it.
 *
 * @param {string|null|undefined} did
 * @param {string|null|undefined} extension
 * @returns {string}
 */
export const formatDirectLine = (did, extension) => {
  const formatted = formatPhoneForDisplay(did);
  const ext = String(extension || '').trim();
  if (!formatted) return ext ? `ext. ${ext}` : '';
  return ext ? `${formatted} ext. ${ext}` : formatted;
};

/**
 * Digits suitable for `tel:` hrefs (no spaces). Prefers +65 for 8-digit local.
 *
 * @param {string|null|undefined} phone
 * @returns {string} Empty string when no usable digits
 */
export const phoneToTelHref = (phone) => {
  if (!phone) return '';
  const local = toLocalSgDigits(phone);
  if (local.length === 8) return `tel:+65${local}`;
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  return digits.startsWith('65') ? `tel:+${digits}` : `tel:${digits}`;
};
